import React from 'react';

const printStyles = `
  @page { size: A4; margin: 8mm; }
  @media print {
    html, body {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      background-color: white !important;
      margin: 0 !important; padding: 0 !important;
      overflow: visible !important;
    }
    .quot-wrapper { box-shadow: none !important; margin: 0 auto !important; width: 210mm !important; border: none !important; }
    .no-print { display: none !important; }
  }
`;

const formatINR = (value: any) => {
  const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : Number(value || 0);
  if (isNaN(num)) return '';
  return new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
};

const numberToWords = (n: number): string => {
  if (n === 0) return 'Rupees Zero Only';
  const ones = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const tens = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  const toW = (x: number): string => {
    if (x === 0) return '';
    if (x < 20) return ones[x] + ' ';
    if (x < 100) return tens[Math.floor(x / 10)] + (x % 10 ? ' ' + ones[x % 10] : '') + ' ';
    if (x < 1000) return ones[Math.floor(x / 100)] + ' Hundred ' + toW(x % 100);
    if (x < 100000) return toW(Math.floor(x / 1000)) + 'Thousand ' + toW(x % 1000);
    if (x < 10000000) return toW(Math.floor(x / 100000)) + 'Lakh ' + toW(x % 100000);
    return toW(Math.floor(x / 10000000)) + 'Crore ' + toW(x % 10000000);
  };
  return 'Rupees ' + toW(Math.floor(Math.abs(n))).trim() + ' Only';
};

const fmtDate = (iso: string) => {
  if (!iso) return '';
  const p = iso.split('-');
  return p.length === 3 ? `${p[2].padStart(2, '0')}-${p[1].padStart(2, '0')}-${p[0]}` : iso;
};

const QuotationPrint = ({ data }: { data: any }) => {
  if (!data) return null;

  const b = '1px solid #000';
  const lightBg = '#dce6f0';
  const items = data.items || [];
  const totalQty = items.reduce((s: number, it: any) => s + (it.qty || 0), 0);
  const amountInWords = numberToWords(data.totalAmount || 0);

  return (
    <>
      <style>{printStyles}</style>
      <div className="quot-wrapper mx-auto bg-white"
        style={{ width: '210mm', boxSizing: 'border-box', fontSize: '13px', lineHeight: '1.4', padding: '6px', fontFamily: 'Arial, sans-serif', color: '#000' }}>
        <div style={{ border: b, display: 'flex', flexDirection: 'column' }}>

          {/* Company Header */}
          <div style={{ borderBottom: b, textAlign: 'center', padding: '10px 12px' }}>
            <div style={{ fontWeight: 800, fontSize: '22px', letterSpacing: '1px' }}>{data.companyName || ''}</div>
            {data.companyAddress && <div style={{ fontSize: '12px', marginTop: '2px' }}>Address: {data.companyAddress}</div>}
            <div style={{ fontSize: '11px', marginTop: '2px' }}>
              {data.companyGstin && <span>GSTIN: {data.companyGstin}</span>}
              {data.companyState && <span>, State: {data.companyStateCode || ''}- {data.companyState}</span>}
              {data.companyMobile && <span>, Mob: {data.companyMobile}</span>}
            </div>
          </div>

          {/* Quotation Copy title */}
          <div style={{ borderBottom: b, textAlign: 'center', padding: '6px', fontWeight: 700, fontSize: '15px', backgroundColor: lightBg }}>
            Quotation Copy
          </div>

          {/* Bill To + Quotation Details */}
          <div style={{ display: 'flex', borderBottom: b }}>
            <div style={{ width: '60%', borderRight: b, padding: '8px 12px' }}>
              <div style={{ fontWeight: 700, fontSize: '13px' }}>Bill To:</div>
              <div style={{ fontWeight: 700, fontSize: '14px', marginTop: '2px' }}>{data.partyName || ''}</div>
              {data.partyAddress && <div style={{ fontSize: '12px' }}>{data.partyAddress}</div>}
            </div>
            <div style={{ width: '40%', padding: '8px 12px' }}>
              <div style={{ fontWeight: 700, fontSize: '13px', textAlign: 'right' }}>Quotation Details</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', fontSize: '12px' }}>
                <span>Quotation No :</span>
                <span style={{ fontWeight: 700 }}>{data.quotationNumber || ''}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2px', fontSize: '12px' }}>
                <span>Date:</span>
                <span style={{ fontWeight: 700 }}>{fmtDate(data.date)}</span>
              </div>
            </div>
          </div>

          {/* Items Table */}
          <div>
            {/* Header */}
            <div style={{ display: 'flex', borderBottom: b, backgroundColor: lightBg, fontWeight: 700, fontSize: '12px' }}>
              <div style={{ width: '5%', padding: '6px 4px', textAlign: 'center', borderRight: b }}>#</div>
              <div style={{ width: '37%', padding: '6px 8px', borderRight: b }}>Item Name</div>
              <div style={{ width: '12%', padding: '6px 4px', textAlign: 'center', borderRight: b }}>HSN/SAC</div>
              <div style={{ width: '10%', padding: '6px 4px', textAlign: 'center', borderRight: b }}>Qty</div>
              <div style={{ width: '16%', padding: '6px 4px', textAlign: 'right', borderRight: b }}>Rate/Unit</div>
              <div style={{ width: '20%', padding: '6px 8px', textAlign: 'right' }}>Amount</div>
            </div>
            {/* Rows */}
            {items.map((item: any, idx: number) => (
              <div key={idx} style={{ display: 'flex', borderBottom: b, fontSize: '12px' }}>
                <div style={{ width: '5%', padding: '5px 4px', textAlign: 'center', borderRight: b }}>{idx + 1}</div>
                <div style={{ width: '37%', padding: '5px 8px', fontWeight: 600, borderRight: b }}>{item.name || ''}</div>
                <div style={{ width: '12%', padding: '5px 4px', textAlign: 'center', borderRight: b }}>{item.hsn || ''}</div>
                <div style={{ width: '10%', padding: '5px 4px', textAlign: 'center', fontWeight: 700, borderRight: b }}>{item.qty || ''}</div>
                <div style={{ width: '16%', padding: '5px 4px', textAlign: 'right', borderRight: b }}>{formatINR(item.rate)}</div>
                <div style={{ width: '20%', padding: '5px 8px', textAlign: 'right', fontWeight: 700 }}>{formatINR(item.amount)}</div>
              </div>
            ))}
            {/* Empty rows to fill space */}
            {items.length < 8 && Array.from({ length: 8 - items.length }).map((_, i) => (
              <div key={`e${i}`} style={{ display: 'flex', borderBottom: b, fontSize: '12px', minHeight: '24px' }}>
                <div style={{ width: '5%', borderRight: b }}>&nbsp;</div>
                <div style={{ width: '37%', borderRight: b }}>&nbsp;</div>
                <div style={{ width: '12%', borderRight: b }}>&nbsp;</div>
                <div style={{ width: '10%', borderRight: b }}>&nbsp;</div>
                <div style={{ width: '16%', borderRight: b }}>&nbsp;</div>
                <div style={{ width: '20%' }}>&nbsp;</div>
              </div>
            ))}
          </div>

          {/* Total Row */}
          <div style={{ display: 'flex', borderBottom: b, backgroundColor: lightBg, fontWeight: 700, fontSize: '13px' }}>
            <div style={{ width: '5%', borderRight: b, padding: '6px 4px' }}></div>
            <div style={{ width: '37%', borderRight: b, padding: '6px 8px' }}>Total</div>
            <div style={{ width: '12%', borderRight: b, padding: '6px 4px' }}></div>
            <div style={{ width: '10%', borderRight: b, padding: '6px 4px', textAlign: 'center', fontWeight: 700 }}>{totalQty || ''}</div>
            <div style={{ width: '16%', borderRight: b, padding: '6px 4px' }}></div>
            <div style={{ width: '20%', padding: '6px 8px', textAlign: 'right' }}>₹ {formatINR(data.subtotal)}</div>
          </div>

          {/* CGST / SGST */}
          {(data.cgstRate > 0 || data.sgstRate > 0) && (
            <>
              {data.cgstRate > 0 && (
                <div style={{ display: 'flex', borderBottom: b, fontSize: '12px' }}>
                  <div style={{ width: '80%', padding: '4px 12px', textAlign: 'right', fontStyle: 'italic' }}>CGST @{data.cgstRate}%</div>
                  <div style={{ width: '20%', padding: '4px 8px', textAlign: 'right', fontWeight: 600 }}>{formatINR(data.cgstAmount)}</div>
                </div>
              )}
              {data.sgstRate > 0 && (
                <div style={{ display: 'flex', borderBottom: b, fontSize: '12px' }}>
                  <div style={{ width: '80%', padding: '4px 12px', textAlign: 'right', fontStyle: 'italic' }}>SGST @{data.sgstRate}%</div>
                  <div style={{ width: '20%', padding: '4px 8px', textAlign: 'right', fontWeight: 600 }}>{formatINR(data.sgstAmount)}</div>
                </div>
              )}
            </>
          )}

          {/* Grand Total */}
          <div style={{ display: 'flex', borderBottom: b, fontSize: '12px' }}>
            <div style={{ width: '80%', padding: '6px 12px', fontWeight: 600 }}>All prices are exclusive of GST.</div>
            <div style={{ width: '20%', padding: '6px 8px', textAlign: 'right', fontWeight: 800, fontSize: '14px' }}>₹ {formatINR(data.totalAmount)}</div>
          </div>

          {/* Amount in Words */}
          <div style={{ borderBottom: b, padding: '6px 12px' }}>
            <div style={{ fontSize: '10px', color: '#666' }}>Quotation Amount (in Words):</div>
            <div style={{ fontWeight: 700, fontSize: '13px', fontStyle: 'italic' }}>{amountInWords}</div>
          </div>

          {/* Terms */}
          {data.termsAndConditions && (
            <div style={{ borderBottom: b, padding: '6px 12px' }}>
              <div style={{ fontWeight: 700, fontSize: '12px', backgroundColor: lightBg, margin: '-6px -12px 4px', padding: '4px 12px' }}>Terms & Conditions:</div>
              <div style={{ fontSize: '12px' }}>{data.termsAndConditions}</div>
            </div>
          )}

          {/* Bank Details + Signature */}
          <div style={{ display: 'flex', minHeight: '100px' }}>
            <div style={{ width: '55%', borderRight: b, padding: '8px 12px' }}>
              {(data.bankName || data.bankAccountNo || data.bankIfsc) && (
                <>
                  <div style={{ fontWeight: 700, fontSize: '12px', backgroundColor: lightBg, margin: '-8px -12px 6px', padding: '4px 12px' }}>Bank Details</div>
                  <table style={{ fontSize: '12px', borderCollapse: 'collapse' }}>
                    <tbody>
                      {data.bankName && <tr><td style={{ paddingRight: '8px', paddingBottom: '2px' }}>Name :</td><td style={{ fontWeight: 700 }}>{data.bankName}</td></tr>}
                      {data.bankAccountNo && <tr><td style={{ paddingRight: '8px', paddingBottom: '2px' }}>Account No.:</td><td style={{ fontWeight: 700 }}>{data.bankAccountNo}</td></tr>}
                      {data.bankIfsc && <tr><td style={{ paddingRight: '8px' }}>IFSC Code :</td><td style={{ fontWeight: 700 }}>{data.bankIfsc}</td></tr>}
                    </tbody>
                  </table>
                </>
              )}
            </div>
            <div style={{ width: '45%', padding: '8px 12px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <div style={{ textAlign: 'right', fontWeight: 700, fontSize: '12px' }}>For : {data.companyName || ''}</div>
              {data.signatureImage && (
                <div style={{ textAlign: 'right', margin: '4px 0' }}>
                  <img src={data.signatureImage} alt="Signature" style={{ maxHeight: '70px', maxWidth: '150px', marginLeft: 'auto', objectFit: 'contain', display: 'block' }} />
                </div>
              )}
              <div style={{ textAlign: 'right', fontSize: '11px', fontWeight: 600, borderTop: '1px solid #ccc', paddingTop: '4px' }}>Authorized Signatory</div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default QuotationPrint;
